import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getContractDetailBasicInfo } from "src/api/contract/contract-api";
import { ContractStep } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { buildVisitorSelfRegistUrl } from "src/utils/serviceAppUrl";
import { Order, PageMeta, Select } from "src/api/public-types";
import { getParkingCheckServiceAsync, getVisitorsAsync } from "src/api/visitor/visitor-api";
import { Ac2AccessCode, ContractVisitorListModel } from "src/api/visitor/visitor-types";
import { BaseButton, BaseInput, BaseInputWithSearch, BaseModal, BaseSelect, BaseTable } from "src/components";
import RangeDatepickerWithSelect from "src/components/RangeDatepickerWithSelect";
import SearchResetButton from "src/components/SearchResetButton";
import ViewPC from "src/components/ViewPC";
import ContractSelectModal from "src/components/contract/ContractSelectModal";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useQueryParams } from "src/hooks/useQueryParams";
import PagePath from "src/pagePath.json";
import { useModal } from "src/recoil/modalState/hook";
import { isMobilePath, toPhoneSearchValue } from "src/utils/common-util";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { INVITEABLE_CONTRACT_STEP } from "../constants";
import useCsnkey from "../hooks/useCsnkey";
import { Modal } from "../visitor-types";
import ExcelDownloadButton from "./ExcelDownloadButton";
import { columns, afterColumns, parkingColumns, mobileColumns, excelOnlyColumns, excelVisitProcessStatusColumn } from "./columns/Columns";
import { useRecoilValue } from "recoil";
import { globalPartnerState } from "src/recoil/partners/atom";

// url query parameter 타입
type QueryParams = {
  page?: number;
  size?: number;
  sort?: {
    orders?: Array<Order>;
  };
  visitStatus?: string;
  searchType?: string;
  searchValue?: string;
  partnerId?: string;
  searchStartTime?: string;
  searchEndTime?: string;
  visitType?: string;
  visitPurpose?: string;
  contractId?: string;
  contractApplyNumber?: string;
  /** 지정검색 입력칸 표기용. 목록 조회 필터는 contractApplyNumber 를 그대로 사용한다 */
  contractManageId?: string;
  contractStep?: ContractStep;
  approvalStatus?: string;
  memberApprovalStatus?: string;
  tenantManagerApprovalStatus?: string;
  invitationChannel?: string;
  contractVisitorIds?: string;
  searchTimeType?: string;
};

// 검색옵션이 "방문자 ID"(VISITOR_ID)인 경우 backend는 searchType/searchValue 대신
// contractVisitorIds 파라미터로 받음. 그 외는 기존 searchType/searchValue 사용.
const buildSearchQuery = (searchType: string, searchValue: string) => {
  if (searchType === "VISITOR_ID") {
    return { searchType: "", searchValue: "", contractVisitorIds: searchValue };
  }
  return { searchType, searchValue: toPhoneSearchValue(searchType, searchValue, "VISITOR_MOBILE_NUMBER"), contractVisitorIds: "" };
};

// 방문상태 options
const visitStatusOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "대기", value: "VISIT_PENDING" },
  { label: "사용", value: "VISIT_USE" },
  { label: "취소", value: "VISIT_DELETE" },
  { label: "종료", value: "VISIT_CLOSE" },
];

// 방문목적 options
const visitPurposeOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "업무 미팅", value: "WORK_MEETING" },
  { label: "지인 방문", value: "FRIEND_VISIT" },
  { label: "퀵/택배", value: "PACKAGE_DELIVERY" },
  { label: "음식 배달", value: "FOOD_DELIVERY" },
  { label: "기타", value: "ETC" },
];

// 방문자 승인상태 options
const approvalStatusOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "자동승인", value: "APPROVAL_AUTO" },
  { label: "승인대기", value: "APPROVAL_PENDING" },
  { label: "승인완료", value: "APPROVAL_COMPLETE" },
  { label: "반려", value: "APPROVAL_REJECT" },
];

// 기간 옵션
const calendarDateOption: Select[] = [
  { label: "전체", value: "" },
  { label: "방문 시작일", value: "VISIT_START_TIME" },
  { label: "방문 종료일", value: "VISIT_END_TIME" },
  { label: "초대일", value: "INVITATION_DATE" },
];

// 접근 경로 options
const invitationChannelOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "TaapSpace", value: "TAAP_SPACE" },
  { label: "Mobile App", value: "MOBILE_APP" },
  { label: "Ctrl.room", value: "CTRL_ROOM" },
  { label: "Self Registration", value: "SELF_REGIST" },
];

// 검색대상 options
const searchTypeOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "방문신청번호", value: "VISIT_APPLY_NUMBER" },
  { label: "방문자 ID", value: "VISITOR_ID" },
  { label: "방문자명", value: "VISITOR_NAME" },
  { label: "방문자 휴대폰 번호", value: "VISITOR_MOBILE_NUMBER" },
  { label: "방문자 이메일", value: "VISITOR_EMAIL" },
  { label: "방문자 소속회사", value: "VISITOR_ORGANIZATION_NAME" },
  { label: "주차 차량 번호", value: "VISITOR_CAR_NUMBER" },
  { label: "초대자 이메일", value: "INVITEE_MEMBER_ID" },
  { label: "초대자 회원번호", value: "INVITEE_MEMBER_NO" },
  // TODO(BE): 계약관리 ID 검색은 방문자 목록 API 의 searchType 에 CONTRACT_MANAGE_ID 가 추가 배포된 뒤 동작한다.
  { label: "계약관리 ID", value: "CONTRACT_MANAGE_ID" },
];

// 정렬기능 숨김
const disabledSortHeaders: string[] = [
  "contractVisitorId", // 방문자 ID
  "visitorName", // 방문자 명
  "visitorMobileNumber", // 방문자 휴대폰
  "visitorKeyCardButton", // 키카드 할당
  "visitorKeyCard", // 키카드 번호
  "visitorCarNumber",
  "inviteeMemberId", // 초대자
  "inviteeMemberNo", // 초대자 회원번호
  "invitationChannel", // 초대경로
  "modifiedDate", // 수정일
  "contractManageId", // 계약관리 ID
];

/* 
  방문자 > 목록화면
*/

const VisitorList = () => {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  // 기간 선택값이 없을 때 사용자가 고른 기간 옵션을 임시 보관 (URL 미반영 → API 호출 없음)
  const [pendingSearchTimeType, setPendingSearchTimeType] = useState<string>("");
  const [mobileSearchMenuOpen, setMobileSearchMenuOpen] = useState<boolean>(false);
  const { isAuthority } = usePartnerAuthority();
  const params = useParams();

  const { setBaseModal } = useModal();

  const { queryParams, queryParamsNavigate } = useQueryParams<QueryParams>({
    page: 0,
    size: 20,
    sort: {
      orders: [{ property: "createdDate", direction: "DESC" }],
    },
  });

  const [searchInput, setSearchInput] = useState({
    searchType: "",
    searchValue: "",
  });

  const { getFixedCsnMutationAsync } = useCsnkey();
  const { executeAsync: getVisitors } = useApiOperation(getVisitorsAsync);
  const { executeAsync: getContractDetailBasicInfoAsync } = useApiOperation(getContractDetailBasicInfo);
  const location = useLocation();
  const [alertModal, setAlertModal] = useState<Modal>({
    isOpen: false,
  });

  const [contractSelectModal, setContractSelectModal] = useState<Modal>({
    isOpen: false,
  });

  const [visitors, setVisitors] = useState<ContractVisitorListModel[]>();
  const [pageMeta, setPageMeta] = useState<PageMeta>();

  //지정검색 입력칸 표기용 계약관리 ID.
  //계약관리 ID 도입 전에 공유된 URL 은 contractId/contractApplyNumber 만 들고 오므로 contractId 로 보정한다
  const [contractManageIdLabel, setContractManageIdLabel] = useState("");
  const partner = useRecoilValue(globalPartnerState);
  const isParkingFeatureActive = partner?.features
    ?.filter((feature) => {
      const parkingFeature = ["FEATURE_PARKING_KPS", "FEATURE_PARKING_IPARKING", "FEATURE_PARKING_AMANO"];
      return parkingFeature.includes(feature.name);
    })
    .some((feature) => feature.active);
  const isVisitorSelfRegist = partner?.features?.some((feature) => feature.name === "FEATURE_VISITOR_SELF_REGIST" && feature.active) || false;

  const onClickVisitorSelfRegist = () => {
    if (!partner?.code || !partner?.serviceApp) return;
    window.open(buildVisitorSelfRegistUrl(partner.code, partner.serviceApp), "_blank");
  };

  useEffect(() => {
    if (queryParams?.contractManageId) {
      setContractManageIdLabel(String(queryParams.contractManageId));
      return;
    }
    if (!queryParams?.contractId) {
      setContractManageIdLabel("");
      return;
    }
    (async () => {
      const result = await getContractDetailBasicInfoAsync({ id: Number(queryParams.contractId) }).catch(() => undefined);
      if (result) {
        setContractManageIdLabel(String(result.data.data.contractManageId ?? ""));
      }
    })();
  }, [queryParams?.contractManageId, queryParams?.contractId, getContractDetailBasicInfoAsync]);

  useEffect(() => {
    // queryParams 기준 방문자 리스트 호출

    (async () => {
      const result = await getVisitors(queryParams);

      setVisitors(result.data.data.contractVisitor);
      setPageMeta(result.data.meta.pageMeta);
    })();
  }, [queryParams]);

  useEffect(() => {
    // queryParams 검색 옵션, 검색어 세팅
    // contractVisitorIds가 있으면 "방문자 ID" 검색으로 복원
    if (queryParams.contractVisitorIds) {
      setSearchInput({ searchType: "VISITOR_ID", searchValue: queryParams.contractVisitorIds });
      return;
    }
    setSearchInput({ searchType: queryParams.searchType ?? "", searchValue: queryParams.searchValue ?? "" });
  }, [queryParams.searchType, queryParams.searchValue, queryParams.contractVisitorIds]);

  useEffect(() => {
    // queryParams 기준 날짜 세팅
    const dump = _.cloneDeep(dateRange);
    if (queryParams?.searchStartTime !== undefined && queryParams?.searchStartTime !== "") {
      dump[0] = moment(queryParams?.searchStartTime).toDate();
    } else {
      dump[0] = null;
    }
    if (queryParams?.searchEndTime !== undefined && queryParams?.searchEndTime !== "") {
      dump[1] = moment(queryParams?.searchEndTime).toDate();
    } else {
      dump[1] = null;
    }
    setDateRange(dump);
  }, [queryParams.searchEndTime, queryParams.searchStartTime]);

  // 날짜 검색
  const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
    setDateRange(dateRange);
    let start = "";
    let end = "";

    if (dateRange[0] === null && dateRange[1] === null) {
      queryParamsNavigate({ ...queryParams, searchStartTime: undefined, searchEndTime: undefined, searchTimeType: undefined, page: 0 });
      setPendingSearchTimeType("");
      return;
    }

    if (dateRange[0] !== null) {
      start = moment(dateRange[0]).format("YYYY-MM-DD") + "T00:00:00.000+09:00";
    }
    if (dateRange[1] !== null) {
      end = moment(dateRange[1]).format("YYYY-MM-DD") + "T23:59:59.999+09:00";

      queryParamsNavigate({
        ...queryParams,
        searchStartTime: start,
        searchEndTime: end,
        searchTimeType: pendingSearchTimeType || queryParams.searchTimeType,
        page: 0,
      });
      setPendingSearchTimeType("");
    }
  };

  const goFormPage = (contractApplyNumber: string) =>
    navigate(
      PagePath.visitor.add.replace(":contractApplyNumber", contractApplyNumber).replace(":contractId", queryParams?.contractId?.toString() || ""),
    );

  const clickVisitorInvitation = async () => {
    const contractApplyNumber = queryParams?.contractApplyNumber;
    let contractStep = queryParams?.contractStep;

    if (!contractApplyNumber) {
      setAlertModal({ isOpen: true, message: "계약을 선택해주세요." });
      return;
    }

    if (!contractStep) {
      // 신청계약을 통해 접근한 경우 API 요청 후 계약 상태 체크
      const contract = await getContractDetailBasicInfoAsync({ id: Number(queryParams?.contractId) }).then((res) => res?.data?.data);
      contractStep = contract.contractStep as ContractStep;
    }

    if (!contractStep || !INVITEABLE_CONTRACT_STEP.includes(contractStep as ContractStep)) {
      setAlertModal({ isOpen: true, message: "초대할 수 없는 계약 상태입니다." });
      return;
    }

    goFormPage(contractApplyNumber);
  };

  /**
   * 방문자 목록을 엑셀 파일로 다운로드하는 함수
   * 데이터가 많은 경우 분할하여 가져오는 방식 사용
   */
  const handleClickExcelDownloadButton = async () => {
    const sizeCheckResult = await getVisitors({ ...queryParams, page: 0, size: 1 });
    const totalCount = sizeCheckResult.data.meta?.pageMeta?.totalElements || 0;

    const chunkSize = 100; // 한 번에 조회할 데이터 수
    const chunkCount = Math.ceil(totalCount / chunkSize);

    const apiCalls = [];
    for (let i = 0; i < chunkCount; i++) {
      apiCalls.push(
        getVisitors({
          ...queryParams,
          page: i,
          size: chunkSize,
        }),
      );
    }

    // 병렬로 데이터 수집 후 병합
    const responses = await Promise.all(apiCalls);
    const allData: (ContractVisitorListModel & { visitorKeyCard?: string })[] = responses.flatMap(
      (response) => response.data.data.contractVisitor || [],
    );

    // contractVisitorId 목록 추출 (null/undefined 제거)
    const visitorIds = allData.map((item) => item.contractVisitorId?.toString()).filter((id): id is string => !!id);

    // ID를 청크 단위로 나누어 여러 번 호출
    const csnIdChunks = _.chunk(visitorIds, chunkSize);

    const csnResponses = await Promise.all(
      csnIdChunks.map((chunkIds) =>
        getFixedCsnMutationAsync({
          contractVisitorIds: chunkIds,
        }),
      ),
    );

    const csnkeyList = csnResponses.flatMap((res) => res?.data?.data?.content || []);

    // csnkeyList에서 contractVisitorId를 키로 하고 accessToken을 값으로 하는 매핑 객체 생성
    const visitorIdToAccessTokenMap = csnkeyList?.reduce((acc: Record<string, string>, item: Ac2AccessCode) => {
      if (item.contractVisitorId && item.accessToken) {
        acc[item.contractVisitorId.toString()] = item.accessToken;
      }
      return acc;
    }, {});

    // 엑셀에서 visitorKeyCard 컬럼에 cardNumber(accessToken)를 출력할 수 있도록 데이터에 주입
    // Column에서 훅을 사용중이라 이렇게 처리함

    allData.forEach((item) => {
      const key = item.contractVisitorId?.toString() || "";
      if (!key) return;
      const cardNumber = visitorIdToAccessTokenMap?.[key];
      if (cardNumber) {
        item.visitorKeyCard = cardNumber;
      }
    });

    // 엑셀 컬럼: afterColumns의 회원번호(index 4)와 계약관리 ID(index 5) 사이에 excelOnlyColumns 삽입
    const memberNoIdx = afterColumns.findIndex((col: any) => col.accessor === "inviteeMemberNo");
    const excelAfterColumns = [...afterColumns.slice(0, memberNoIdx + 1), ...excelOnlyColumns, ...afterColumns.slice(memberNoIdx + 1)];

    // 엑셀 컬럼: 방문상태(visitStatus) 컬럼 앞에 방문 처리상태 컬럼 삽입
    const visitStatusIdx = columns.findIndex((col: any) => col.accessor === "visitStatus");
    const excelColumns = [...columns.slice(0, visitStatusIdx), excelVisitProcessStatusColumn, ...columns.slice(visitStatusIdx)];

    const fileNamePrefix =
      dateRange[0] && dateRange[1] ? `방문자-${moment(dateRange[0]).format("YYYYMMDD")}~${moment(dateRange[1]).format("YYYYMMDD")}` : `방문자-전체`;

    await downloadExcelByColumns<ContractVisitorListModel>({
      data: allData,
      columns: isParkingFeatureActive ? [...excelColumns, ...parkingColumns, ...excelAfterColumns] : [...excelColumns, ...excelAfterColumns],
      fileNamePrefix,
      excludeAccessors: isAuthority("r", "BASIC") ? ["visitorKeyCardButton"] : ["visitorKeyCardButton", "visitorKeyCard"],
      onNoData: () => {
        setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
      },
      onSuccess: () => {
        setBaseModal({
          isOpen: true,
          title: "엑셀 다운로드가 완료되었습니다.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
      },
      onError: (error) => {
        setBaseModal({
          isOpen: true,
          title: "엑셀 다운로드에 실패하였습니다.",
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
      },
    });
  };
  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        {isMobilePath(location.pathname) && (
          <div className="mobile-list-tab">
            <div className="mobile-list-tab__tabs">
              <p>방문자</p>
            </div>
          </div>
        )}
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents first">
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={contractManageIdLabel}
                    placeholder="계약관리 ID"
                    onClick={() => setContractSelectModal({ isOpen: true })}
                    onClearClick={() =>
                      queryParamsNavigate({
                        ...queryParams,
                        contractId: "",
                        contractApplyNumber: "",
                        contractManageId: "",
                        contractStep: "" as any,
                        page: 0,
                      })
                    }
                    readonly
                  />
                </div>
                {isMobilePath(location.pathname) && (
                  <button
                    className={`deep-search only-text-btn ic-chevron ${mobileSearchMenuOpen ? "--active" : "no"}`}
                    onClick={() => {
                      setMobileSearchMenuOpen((prev) => !prev);
                    }}
                  >
                    자세히 검색
                  </button>
                )}
              </div>
            </section>
            {!isMobilePath(location.pathname) ? (
              <section>
                <div className="left-area__index">
                  <span>조건검색</span>
                </div>

                <div className="left-area__contents">
                  <div>
                    <BaseInputWithSearch
                      type="text"
                      selectWidth={150}
                      selectValue={searchInput.searchType}
                      inputValue={searchInput.searchValue}
                      stateOptions={searchTypeOptions}
                      setStateValue={(searchType: string) => {
                        setSearchInput((prev) => ({ ...prev, searchType: searchType }));
                      }}
                      onChange={(searchValue: string, e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchInput((prev) => ({ ...prev, searchValue: searchValue }))
                      }
                      onKeyUp={() =>
                        queryParamsNavigate({
                          ...queryParams,
                          ...buildSearchQuery(searchInput.searchType, searchInput.searchValue),
                          page: 0,
                        })
                      }
                      onSearchClick={() =>
                        queryParamsNavigate({
                          ...queryParams,
                          ...buildSearchQuery(searchInput.searchType, searchInput.searchValue),
                          page: 0,
                        })
                      }
                      onClearClick={() => queryParamsNavigate({ ...queryParams, searchType: "", searchValue: "", contractVisitorIds: "", page: 0 })}
                    />
                  </div>

                  <div className="minmax160">
                    <BaseSelect
                      placeholder="이용자 승인상태"
                      value={queryParams.memberApprovalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(memberApprovalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          memberApprovalStatus: memberApprovalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="minmax200">
                    <BaseSelect
                      placeholder="이용자 마스터 승인상태"
                      value={queryParams.tenantManagerApprovalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(tenantManagerApprovalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          tenantManagerApprovalStatus: tenantManagerApprovalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="minmax160">
                    <BaseSelect
                      placeholder="관리자 승인상태"
                      value={queryParams.approvalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(approvalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          approvalStatus: approvalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="minmax160">
                    <BaseSelect
                      placeholder="방문 상태"
                      value={queryParams.visitStatus}
                      stateOptions={visitStatusOptions}
                      setStateValue={(visitStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          visitStatus: visitStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="minmax160">
                    <BaseSelect
                      placeholder="방문 목적"
                      value={queryParams.visitPurpose}
                      stateOptions={visitPurposeOptions}
                      setStateValue={(visitPurpose: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          visitPurpose: visitPurpose,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="minmax160">
                    <BaseSelect
                      placeholder="초대경로"
                      value={queryParams.invitationChannel}
                      stateOptions={invitationChannelOptions}
                      setStateValue={(approvalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          invitationChannel: approvalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>

                  <div className="">
                    <RangeDatepickerWithSelect
                      stateOptions={calendarDateOption}
                      selectValue={
                        queryParams.searchStartTime && queryParams.searchEndTime ? queryParams.searchTimeType || "" : pendingSearchTimeType
                      }
                      setStateValue={(searchTimeType: string) => {
                        if (queryParams.searchStartTime && queryParams.searchEndTime) {
                          queryParamsNavigate({ ...queryParams, searchTimeType, page: 0 });
                        } else {
                          // 기간(날짜)이 선택되지 않은 상태에서는 API 호출을 막고 로컬에만 보관
                          setPendingSearchTimeType(searchTimeType);
                        }
                      }}
                      dateRange={dateRange}
                      onChange={handleOnDateRangeChange}
                      onClearClick={() => {
                        queryParamsNavigate({
                          ...queryParams,
                          searchStartTime: undefined,
                          searchEndTime: undefined,
                          searchTimeType: undefined,
                          page: 0,
                        });
                        setPendingSearchTimeType("");
                      }}
                    />
                  </div>
                  <SearchResetButton />
                </div>
              </section>
            ) : isMobilePath(location.pathname) && mobileSearchMenuOpen ? (
              <section className="d-flex">
                <div className="left-area__index">
                  <span>조건검색</span>
                </div>
                <div className="left-area__contents">
                  <div className="w-100 mr0">
                    <RangeDatepickerWithSelect
                      stateOptions={calendarDateOption}
                      selectValue={
                        queryParams.searchStartTime && queryParams.searchEndTime ? queryParams.searchTimeType || "" : pendingSearchTimeType
                      }
                      setStateValue={(searchTimeType: string) => {
                        if (queryParams.searchStartTime && queryParams.searchEndTime) {
                          queryParamsNavigate({ ...queryParams, searchTimeType, page: 0 });
                        } else {
                          // 기간(날짜)이 선택되지 않은 상태에서는 API 호출을 막고 로컬에만 보관
                          setPendingSearchTimeType(searchTimeType);
                        }
                      }}
                      dateRange={dateRange}
                      onChange={handleOnDateRangeChange}
                      onClearClick={() => {
                        queryParamsNavigate({
                          ...queryParams,
                          searchStartTime: undefined,
                          searchEndTime: undefined,
                          searchTimeType: undefined,
                          page: 0,
                        });
                        setPendingSearchTimeType("");
                      }}
                    />
                  </div>
                  <div className="w-100 mr0">
                    <BaseSelect
                      placeholder="이용자 승인상태"
                      value={queryParams.memberApprovalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(memberApprovalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          memberApprovalStatus: memberApprovalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                  <div className="w-100 mr0">
                    <BaseSelect
                      placeholder="이용자 마스터 승인상태"
                      value={queryParams.tenantManagerApprovalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(tenantManagerApprovalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          tenantManagerApprovalStatus: tenantManagerApprovalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                  <div className="w-100 mr0">
                    <BaseSelect
                      placeholder="관리자 승인상태"
                      value={queryParams.approvalStatus}
                      stateOptions={approvalStatusOptions}
                      setStateValue={(approvalStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          approvalStatus: approvalStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                  <div className="w-100 mr0">
                    <BaseSelect
                      placeholder="상태"
                      value={queryParams.visitStatus}
                      stateOptions={visitStatusOptions}
                      setStateValue={(visitStatus: string) => {
                        queryParamsNavigate({
                          ...queryParams,
                          visitStatus: visitStatus,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                  <div className="w-100 mr0">
                    <BaseInputWithSearch
                      type="text"
                      selectWidth={150}
                      selectValue={searchInput.searchType}
                      inputValue={searchInput.searchValue}
                      stateOptions={searchTypeOptions}
                      setStateValue={(searchType: string) => {
                        setSearchInput((prev) => ({ ...prev, searchType: searchType }));
                      }}
                      onChange={(searchValue: string, e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchInput((prev) => ({ ...prev, searchValue: searchValue }))
                      }
                      onKeyUp={() =>
                        queryParamsNavigate({
                          ...queryParams,
                          ...buildSearchQuery(searchInput.searchType, searchInput.searchValue),
                          page: 0,
                        })
                      }
                      onSearchClick={() =>
                        queryParamsNavigate({
                          ...queryParams,
                          ...buildSearchQuery(searchInput.searchType, searchInput.searchValue),
                          page: 0,
                        })
                      }
                      onClearClick={() => queryParamsNavigate({ ...queryParams, searchType: "", searchValue: "", contractVisitorIds: "", page: 0 })}
                    />
                  </div>
                </div>
              </section>
            ) : (
              <></>
            )}
          </div>
          {!isMobilePath(location.pathname) && (
            <div className="right-area flex-row gap10">
              {isAuthority("w") && (
                <BaseButton
                  title="방문자 셀프 등록 서비스"
                  className="color-white icon-newtab"
                  onClick={onClickVisitorSelfRegist}
                  disabled={!isVisitorSelfRegist}
                />
              )}
              {isAuthority("w") && <BaseButton title="+ 방문자 초대" onClick={clickVisitorInvitation} />}
            </div>
          )}
        </div>

        {visitors && (
          <BaseTable
            data={visitors}
            columns={
              isMobilePath(location.pathname)
                ? mobileColumns
                : isParkingFeatureActive
                ? [
                    ...columns,
                    ...parkingColumns,
                    ...afterColumns.filter(
                      (col) => isAuthority("r", "BASIC") || (col.accessor !== "visitorKeyCardButton" && col.accessor !== "visitorKeyCard"),
                    ),
                  ]
                : [
                    ...columns,
                    ...afterColumns.filter(
                      (col) => isAuthority("r", "BASIC") || (col.accessor !== "visitorKeyCardButton" && col.accessor !== "visitorKeyCard"),
                    ),
                  ]
            }
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            totalElements={pageMeta?.totalElements || 0}
            currentSize={Number(pageMeta?.pageRequest?.size) || 20}
            sizeOption={(size) => queryParamsNavigate({ ...queryParams, page: 0, size: size })}
            goPage={(page: number) => queryParamsNavigate({ ...queryParams, page: page, size: pageMeta?.pageRequest?.size })}
            disabledSortHeaders={disabledSortHeaders}
            orders={pageMeta?.pageRequest.sort?.orders}
            setOrders={(orders: Order[]) => queryParamsNavigate({ ...queryParams, sort: { orders: orders } })}
            children={
              !isMobilePath(location.pathname) ? <ExcelDownloadButton onClick={handleClickExcelDownloadButton}>엑셀받기</ExcelDownloadButton> : null
            }
          />
        )}
        {isMobilePath(location.pathname) && <ViewPC path={PagePath.visitor.list.replace(":partnerId", String(params.partnerId))} />}
      </div>

      {/* 확인버튼만 있는 alert 모달 */}
      {alertModal.isOpen && (
        <BaseModal
          isOpen={true}
          btnRightTitle="확인"
          onClick={() => {
            setAlertModal({ isOpen: false });
            setContractSelectModal({ isOpen: true });
          }}
          title={alertModal.message}
        />
      )}
      {contractSelectModal.isOpen && (
        <ContractSelectModal
          onCanceled={() => setContractSelectModal({ isOpen: false })}
          onAdded={(data) => {
            queryParamsNavigate({
              ...queryParams,
              contractId: data[0].contractId.toString(),
              contractApplyNumber: data[0].contractApplyNumber,
              contractManageId: data[0].contractManageId ? String(data[0].contractManageId) : "",
              contractStep: data[0].contractStep,
              page: 0,
            });
            setContractSelectModal({ isOpen: false });
          }}
          defaultCheckedContractList={[{ contractApplyNumber: queryParams?.contractApplyNumber!, contractId: Number(queryParams.contractId) }]}
          contractStepFilter={INVITEABLE_CONTRACT_STEP}
        />
      )}
    </div>
  );
};

export default VisitorList;
